import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap5';
import * as Yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';
import RatingField from 'components/new_ui/Form/RatingField';
import HorizontalTextField from 'components/new_ui/Form/HorizontalTextField';
import HorizontalSelectRadioField from 'components/new_ui/Form/HorizontalSelectRadioField';
import { Rating } from 'react-simple-star-rating';
import axios from 'axios';

import routes from 'app/routes';
import Alert from 'components/new_ui/Alerts/alert';
import IconWithText from 'components/Widgets/IconWithText';
import SubmitButton from 'components/Widgets/SubmitButton';

import Actions from 'components/Members/Programs/Actions';

export default function Form({ programFeedback }) {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [overallRating, setOverallRating] = useState(0);

  const ratingDescription = (rating) => {
    switch (Math.round(rating)) {
      case 1:
        return 'Poor';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Very good';
      case 5:
        return 'Excellent';
      default:
        return null;
    }
  };

  const formSchema = Yup.object().shape({
    feedback: Yup.string().trim().required('Please share what made coaching great for you.'),
    feedback_private: Yup.string().trim().required('Please share what would make your coaching experience better.'),
  });

  const onSubmitHundler = (values, { setSubmitting, _setErrors }) => {
    if (Object.keys(values.questions).some((key) => values.questions[key] === undefined
      || values.questions[key] === null)) {
      setSubmitting(false);

      Alert({
        text: 'Please answer all the questions to save your feedback.',
      });
    } else if (Object.keys(values.questions).every((key) => values.questions[key] === -1)) {
      setSubmitting(false);

      Alert({
        text: 'Please rate at least one question using stars. N/A rating is not allowed to be set for all questions.',
      });
    } else if (!values.recommend_rate) {
      setSubmitting(false);

      Alert({
        text: 'Please rate how likely are you recommend the coaching program to your peers and co-workers.',
      });
    } else {
      const params = {
        rating: { ...values },
      };

      axios
        .post(routes.api.programs.programFeedback(programId), params)
        .then((response) => {
          setSubmitting(false);

          if (response.data.data.attributes.is_share_needed) {
            Actions.googleShare(response.data.data.attributes.stare_feedback_action_id, () => {
              navigate(routes.members.programs.show(programId));
            });
          } else {
            navigate(routes.members.programs.show(programId));
          }
        });
    }
  };

  return (
    <Formik
      validationSchema={formSchema}
      initialValues={{
        questions: Object.fromEntries(
          programFeedback.attributes.questions.map((question) => [question.id, question.rate]),
        ),
        feedback: programFeedback.attributes.feedback.answer,
        feedback_private: programFeedback.attributes.feedback_private.answer,
        recommend_rate: programFeedback.attributes.recommend_rate.answer,
      }}
      onSubmit={onSubmitHundler}
    >
      {({ values, isSubmitting }) => (
        <FormikForm className="needs-validation" noValidate="">
          <section>
            <Row>
              {programFeedback.attributes.questions.map((question) => (
                <Col key={question.id} lg={6}>
                  <RatingField
                    key={`question-${question.id}`}
                    id={`question-${question.id}`}
                    name={`questions['${question.id}']`}
                    label={<span className="fw-semibold">{question.title}</span>}
                    disabled={isSubmitting}
                    onClick={() => {
                      const result = Object.keys(values.questions).filter((key) => (
                        values.questions[key] !== undefined
                        && values.questions[key] !== null && values.questions[key] !== -1
                      )).map((key) => values.questions[key]);

                      if (result.length === 0) {
                        setOverallRating(0);
                      } else {
                        setOverallRating(result.reduce((acc, val) => acc + val, 0) / result.length);
                      }
                    }}
                  />
                </Col>
              ))}
            </Row>

            <Row className="mb-5">
              <Col lg={6}>
                <HorizontalTextField
                  as="textarea"
                  id="feedback"
                  name="feedback"
                  label={(
                    <>
                      <span className="fw-semibold">
                        {programFeedback.attributes.feedback.question}
                      </span>

                      <br />

                      <small>
                        {programFeedback.attributes.feedback.description}
                      </small>
                    </>
                  )}
                  rows={4}
                  disabled={isSubmitting}
                />
              </Col>

              <Col lg={6}>
                <HorizontalTextField
                  as="textarea"
                  id="feedback_private"
                  name="feedback_private"
                  label={(
                    <>
                      <span className="fw-semibold">
                        {programFeedback.attributes.feedback_private.question}
                      </span>

                      <br />

                      <small>
                        {programFeedback.attributes.feedback_private.description}
                      </small>
                    </>
                  )}
                  rows={4}
                  disabled={isSubmitting}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <HorizontalSelectRadioField
                  id="recommend_rate"
                  name="recommend_rate"
                  label={(
                    <span className="fw-semibold">
                      {programFeedback.attributes.recommend_rate.question}
                    </span>
                  )}
                  wrapperClassName="text-md-center"
                  containerClassName="d-grid d-md-block gap-2"
                  containerStyle={{ gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))' }}
                  optionClassName="form-check-inline me-4 mb-3"
                  collection={
                    Array.from(
                      { length: 10 },
                      (_, i) => ({ value: i + 1, label: (i + 1).toString() }),
                    )
                  }
                  disabled={isSubmitting}
                />
              </Col>
            </Row>
          </section>

          <footer className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center text-center">
              <span className="mt-md-1">
                Overall rating:

                <span className="d-block d-md-inline mt-2 mt-md-0 mx-md-3 fw-semibold">
                  {overallRating === 0 ? (
                    <>
                      &mdash;
                    </>
                  ) : (
                    <>
                      {Number.parseFloat(overallRating).toFixed(1)}
                      {' '}
                      (
                      {ratingDescription(overallRating)}
                      )
                    </>
                  )}
                </span>
              </span>

              <Rating
                initialValue={Math.round(overallRating)}
                emptyIcon={<IconWithText iconClassName="star-filled-icon bg-gray-300" />}
                fillIcon={<IconWithText iconClassName="star-filled-icon bg-tertiary-600" />}
                className="d-none d-lg-inline-block"
                emptyClassName="d-flex"
                fillClassName="d-flex"
                readonly
              />
            </div>

            <SubmitButton loading={isSubmitting}>
              Submit
            </SubmitButton>
          </footer>
        </FormikForm>
      )}
    </Formik>
  );
}

Form.propTypes = {
  programFeedback: PropTypes.object.isRequired,
};
